import './search.scss';

import CIcon from '@coreui/icons-react';
import { HubType } from '@transferz/fe-models';
import isEqual from 'lodash/isEqual';
import React, { useEffect, useRef, useState } from 'react';
import { useMediaQuery } from 'usehooks-ts';

import dropdownStyles from '../../components/search/input/locationSearchInputDesktop/LocationSearchInputDesktop.module.scss';
import { useLocalize } from '../../hooks/useLocalize';
import { MultilingualHub } from '../../queries/hub/hooks/useQueryMultilingualHubs';
import {
  LocationSearchInput,
} from './input/locationSearchInput/LocationSearchInput';
import { LocationSearchInputDesktop } from './input/locationSearchInputDesktop/LocationSearchInputDesktop';
import {
  addressSpecFromAutoCompleteAddressItem,
  autoCompleteAddressItemFromAddressSpec,
  autoCompleteAddressItemFromMultilingualHub,
  isAddress,
  isHub,
  multiLingualHubFromAutoCompleteAddressItem,
  SearchLocation,
} from './input/utils';
import { AddressSpec } from './input/utils';

export interface SearchProps {
  hub?: MultilingualHub;
  address?: AddressSpec;
  onAddressChange: (address: AddressSpec | undefined) => void;
  onHubChange: (hub: MultilingualHub | undefined) => void;
  setIsOutbound: (reversed: boolean) => void;
  isOutbound: boolean;
  clearError: (name: string | string[]) => void;
  errors?: Array<{name: string}>;
  styles: any;
  locale: string;
}

export const Search: React.FC<SearchProps> = ({
  hub,
  address,
  onAddressChange,
  onHubChange,
  setIsOutbound: setIsOutbound,
  isOutbound: isOutbound,
  clearError,
  errors,
  styles = {},
  locale,
}) => {
  const isMobile = useMediaQuery('(max-width: 1100px)');
  const { translate: t } = useLocalize();
  const [originLocation, setOriginLocation] = useState<SearchLocation | undefined>();
  const [selectedOriginHub, setSelectedOriginHub] = useState<SearchLocation | undefined>(undefined);
  const [selectedDestinationHub, setSelectedDestinationHub] = useState<SearchLocation | undefined>(undefined);
  const [destinationLocation, setDestinationLocation]
    = useState<SearchLocation | undefined>();

  const [deg, setDeg] = useState(0);
  const [bounds, setBounds] = useState<google.maps.LatLngBoundsLiteral>();

  const switchIconRef = useRef(null) as any;

  const originError = errors?.find(
    (error: any) => (isOutbound && error.name === 'hub')
      || (!isOutbound && error.name === 'address'),
  );
  const destinationError = errors?.find(
    (error: any) => (!isOutbound && error.name === 'hub')
      || (isOutbound && error.name === 'address'),
  );

  useEffect(() => {
    const isOutbound = !isAddress(originLocation);
    setIsOutbound(isOutbound);
    const hubLocation = isOutbound ? originLocation : destinationLocation;
    const addressLocation = !isOutbound ? originLocation : destinationLocation;

    onHubChange(hubLocation && multiLingualHubFromAutoCompleteAddressItem(hubLocation));
    onAddressChange(addressLocation && addressSpecFromAutoCompleteAddressItem(addressLocation));
  }, [originLocation, destinationLocation]);

  useEffect(() => {
    const [hubLocation, setHubLocation] = isOutbound
      ? [originLocation, setOriginLocation]
      : [destinationLocation, setDestinationLocation];
    const [addressLocation, setAddressLocation] = !isOutbound
      ? [originLocation, setOriginLocation]
      : [destinationLocation, setDestinationLocation];
    if (hub && !!hub.position?.lat && !!hub.position?.lng) {
      let newSelectedHub =  {
        hubId: hub.id,
        title: hub.autoCompleteName,
        subtitle: hub.autoCompleteLocation,
        formattedAddress: `${hub.autoCompleteName} ${hub.autoCompleteLocation}`,
        lat: hub.position?.lat,
        lng: hub?.position?.lng,
        type: hub.hubType as HubType,
      };
      if (isOutbound) {
        setSelectedOriginHub(newSelectedHub);
      } else {
        setSelectedDestinationHub(newSelectedHub);
      }
    }

    const newHub = hub && autoCompleteAddressItemFromMultilingualHub(hub);
    !isEqual(newHub, hubLocation) && setHubLocation(newHub);

    if (address?.addressHubInformation) {
      const newAddress = autoCompleteAddressItemFromMultilingualHub(address?.addressHubInformation);
      !isEqual(newAddress, addressLocation) && setAddressLocation(newAddress);
    } else if (address?.address && address?.placeId) {
      const newLocation = autoCompleteAddressItemFromAddressSpec(address);
      !isEqual(addressLocation, newLocation) && setAddressLocation(newLocation);
    } else {
      !isEqual(addressLocation, undefined) && setAddressLocation(undefined);
    }
  }, [hub, address]);

  const customSearchStyles = {
    '--form-field-placeholder-text-color':
      styles.formFieldPlaceholderTextColor || '#51536d',
    '--form-field-text-color': styles.formFieldTextColor || '#5c6873',
  } as React.CSSProperties;

  const switchOriginAndDestination = () => {
    setOriginLocation(destinationLocation);
    setDestinationLocation(originLocation);
    setSelectedOriginHub(selectedDestinationHub);
    setSelectedDestinationHub(selectedOriginHub);
    clearError(['hub', 'address']);
  };

  return (
    <div
      className={`search ${isOutbound && 'search-reversed'}`}
      style={customSearchStyles}
    >
      {isMobile ? (
        <LocationSearchInput
          location={originLocation}
          selectedHub={selectedDestinationHub}
          modalTitle={t('search_origin_modal_title')}
          placeholder={t('search_origin_placeholder')}
          allowAddressSearch={
            !destinationLocation || isHub(destinationLocation)
          }
          setLocation={(location: SearchLocation): void => {
            setOriginLocation(location);
            if (location?.isHub || location === undefined) {
              setSelectedOriginHub(location);
            }
            // if directionality is changed, we need to clear the error of the other location
            isOutbound === !isAddress(location) ? clearError(originError?.name ?? '') : clearError(destinationError?.name ?? '');
          }}
          error={originError && t('search_error_pickup_location')}
          bounds={bounds}
          dataTestId="origin-location"
        />
      ) : (
        <LocationSearchInputDesktop
          placeholder={t('search_origin_placeholder')}
          selectedHub={selectedDestinationHub}
          allowAddressSearch={!destinationLocation || isHub(destinationLocation)}
          location={originLocation}
          locale={locale}
          setLocation={(location: SearchLocation | undefined): void => {
            setOriginLocation(location);
            if (location?.isHub || location === undefined) {
              setSelectedOriginHub(location);
            }
            // if directionality is changed, we need to clear the error of the other location
            isOutbound === !isAddress(location) ? clearError(originError?.name ?? '') : clearError(destinationError?.name ?? '');
          }}
          bounds={bounds}
          error={originError && t('search_error_pickup_location')}
          dataTestId="origin-location"
            styles={dropdownStyles}
            asyncSelectStyles={{
              control: (base: any) => ({
                ...base,
                height: '50px',
                gap: '8px',
                border: `1px solid ${originError && t('search_error_pickup_location') ? '#FC363B' : '#bfcbd4'} !important`,
                borderRadius: '4px',
                boxSizing: 'content-box',
                cursor: 'pointer',
                ...(originError && t('search_error_pickup_location') ? { background: '#FDF0ED' } : {}),
              }),
              menuList: (base: any) => ({
                ...base,
                padding: '0',
                maxHeight: '240px',
              }),
              placeholder: (base: any) => ({
                ...base,
                marginTop: '1px',
              }),
              noOptionsMessage: (base: any) => ({
                ...base,
                color: '#000',
                fontVariantNumeric: 'lining-nums proportional-nums',
                fontFeatureSettings: '\'clig\' off, \'liga\' off',

                fontSize: '14px',
                fontStyle: 'italic',
                fontWeight: 500,
                lineHeight: '20px',
                textAlign: 'left',
              }),
            }}
        />
      )}
      <div
        className="switcher"
        ref={switchIconRef}
        onClick={() => {
          switchOriginAndDestination();
          setDeg(deg + 180);
          switchIconRef.current.style.transform = `rotate(${deg + 180}deg)`;
        }}
      >
        <CIcon size="sm" name="cil-transfer" />
      </div>
      {isMobile ? (
        <LocationSearchInput
          location={destinationLocation}
          selectedHub={selectedOriginHub}
          setLocation={(location: SearchLocation): void => {
            setDestinationLocation(location);
            if (location?.isHub || location === undefined) {
              setSelectedDestinationHub(location);
            }

            /*
             * directionality only depends on the origin location, so we don't
             * need to clear the error of the other location
             */
            clearError(destinationError?.name ?? '');
          }}
          modalTitle={t('search_destination_modal_title')}
          placeholder={t('search_destination_placeholder')}
          allowAddressSearch={!originLocation || isHub(originLocation)}
          error={destinationError && t('search_error_dropoff_location')}
          bounds={bounds}
          dataTestId="destination-location"
        />
      ) : (
        <LocationSearchInputDesktop
          placeholder={t('search_destination_placeholder')}
          allowAddressSearch={!originLocation || isHub(originLocation)}
          selectedHub={selectedOriginHub}
          location={destinationLocation}
          locale={locale}
          setLocation={(location: SearchLocation | undefined): void => {
            setDestinationLocation(location);
            if (location?.isHub || location === undefined) {
              setSelectedDestinationHub(location);
            }

            /*
             * directionality only depends on the origin location, so we don't
             * need to clear the error of the other location
             */
            clearError(destinationError?.name ?? '');
          }}
          bounds={bounds}
          error={destinationError && t('search_error_dropoff_location')}
          dataTestId="destination-location"
            styles={dropdownStyles}
            asyncSelectStyles={{
              control: (base: any) => ({
                ...base,
                height: '50px',
                gap: '8px',
                border: `1px solid ${destinationError && t('search_error_dropoff_location') ? '#FC363B' : '#bfcbd4'} !important`,
                borderRadius: '4px',
                boxSizing: 'content-box',
                cursor: 'pointer',
                ...(destinationError && t('search_error_dropoff_location') ? { background: '#FDF0ED' } : {}),
              }),
              menuList: (base: any) => ({
                ...base,
                padding: '0',
                maxHeight: '240px',
              }),
              placeholder: (base: any) => ({
                ...base,
                marginTop: '1px',
              }),
              noOptionsMessage: (base: any) => ({
                ...base,
                color: '#000',
                fontVariantNumeric: 'lining-nums proportional-nums',
                fontFeatureSettings: '\'clig\' off, \'liga\' off',

                fontSize: '14px',
                fontStyle: 'italic',
                fontWeight: 500,
                lineHeight: '20px',
                textAlign: 'left',
              }),
            }}
        />
      )}
    </div>
  );
};
